import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';

import { useIframeDismissibleOpen } from '@src/hooks/use-close-on-iframe-focus';
import { cn } from '../../lib/utils';

/** Closes on a press inside a web app tab's iframe too, which Radix's own
 *  outside-press dismissal never sees. */
function Popover(props: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  const state = useIframeDismissibleOpen(props);
  return <PopoverPrimitive.Root {...props} {...state} />;
}

const PopoverTrigger = PopoverPrimitive.Trigger;

const PopoverAnchor = PopoverPrimitive.Anchor;

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = 'center', sideOffset = 4, ...props }, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        // The available-height cap + scroll mirrors DropdownMenuContent/SelectContent:
        // without it a popover taller than the gap to the viewport edge is simply cut
        // off. `cn` is tailwind-merge, so a call site with its own `max-h-*` still wins.
        'z-50 max-h-[var(--radix-popover-content-available-height)] w-72 origin-[--radix-popover-content-transform-origin] overflow-y-auto overflow-x-hidden rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        className,
      )}
      {...props}
    />
  </PopoverPrimitive.Portal>
));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor };
